import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * SearchableSelect — a single-value searchable dropdown that integrates with
 * react-hook-form via Controller (value / onChange).
 *
 * Props:
 *   value          - current selected value (must match getOptionValue output)
 *   onChange       - fn(value) called when user picks an option
 *   options        - array of option objects
 *   placeholder    - text shown when nothing is selected (default "Select...")
 *   getOptionLabel - fn(opt) → display string  (default opt.label || opt.name)
 *   getOptionValue - fn(opt) → comparable value (default opt.value ?? opt.id)
 *   disabled       - bool
 *   searchPlaceholder - placeholder for search input (default "Search...")
 *   error          - error message string
 *   label          - label text rendered above the dropdown
 */
const SearchableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  getOptionLabel = (opt) => opt?.label ?? opt?.name ?? String(opt?.id ?? ''),
  getOptionValue = (opt) => opt?.value ?? opt?.id,
  disabled = false,
  searchPlaceholder = 'Search...',
  error,
  label,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus search input when opening
  useEffect(() => {
    if (open) {
      setSearch('');
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((opt) =>
      String(getOptionLabel(opt) ?? '').toLowerCase().includes(term)
    );
  }, [options, search, getOptionLabel]);

  const selectedOption = useMemo(
    () => options.find((opt) => String(getOptionValue(opt)) === String(value)),
    [options, value, getOptionValue]
  );

  const displayText = selectedOption ? getOptionLabel(selectedOption) : placeholder;
  const hasValue = selectedOption != null;

  const handleSelect = (opt) => {
    onChange(getOptionValue(opt));
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <div className="flex flex-col relative" ref={containerRef}>
      {label && <label className="text-xs text-gray-600 mb-1">{label}</label>}

      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`
          border rounded p-2 text-sm w-full flex items-center justify-between text-left
          bg-white focus:outline-none focus:ring-1 focus:ring-blue-500
          disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed
          ${error ? 'border-red-400' : 'border-gray-300'}
        `}
      >
        <span className={`truncate flex-1 ${!hasValue ? 'text-gray-400' : 'text-gray-800 uppercase'}`}>
          {displayText}
        </span>
        <span className="flex items-center gap-1 ml-2 shrink-0">
          {hasValue && !disabled && (
            <XMarkIcon
              className="h-4 w-4 text-gray-400 hover:text-gray-600"
              onClick={handleClear}
            />
          )}
          <ChevronDownIcon
            className={`h-4 w-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </span>
      </button>

      {/* Dropdown */}
      {open && !disabled && (
        <div className="absolute top-full left-0 z-30 mt-1 w-full border rounded bg-white shadow-lg">
          {/* Search input */}
          <div className="sticky top-0 bg-white p-2 border-b">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
              // Prevent the uppercase capture handler on the form from uppercasing the search
              data-case="normal"
            />
          </div>

          {/* Options list */}
          <ul className="max-h-52 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-500">
                {options.length === 0 ? 'No options available' : 'No matching vehicles'}
              </li>
            ) : (
              filteredOptions.map((opt) => {
                const optVal = getOptionValue(opt);
                const isSelected = String(optVal) === String(value);
                return (
                  <li
                    key={optVal}
                    onClick={() => handleSelect(opt)}
                    className={`
                      px-3 py-2 text-sm cursor-pointer uppercase
                      ${isSelected
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'hover:bg-gray-50 text-gray-800'}
                    `}
                  >
                    {getOptionLabel(opt)}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}

      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

export default SearchableSelect;
